import { useRef } from 'preact/hooks';
import { loadNewEpisode, pause, play } from '../../global-state/audio-player';
import { currentPlaylist } from '../../global-state/audio-signals';
import { idToEpisode, usePodcastsStore } from '../../global-state/podcasts';
import { useUserDataStore } from '../../global-state/user-data';
import { Episode, EpisodeCompoundId } from '../../models/podcasts';
import getAdsStatus from '../../utils/ads-status';
import { checkIfPlayed } from '../../utils/check-if-played';
import { relativeDate } from '../../utils/format-date';
import getThumbUrl from '../../utils/get-thumb';
import { classNames } from '../../utils/helpers';
import { trackPlayEpisodeInUpNextClick } from '../../utils/plausible-events';
import { route } from '../../utils/routing';
import { Edge, reorder, useDraggableHandle } from '../../utils/sortable-list';
import { Icon } from '../icon';

export function Playlist() {
  if (!currentPlaylist.value.length)
    return (
      <div class="text-pretty p-4 text-center">
        To add episodes Up Next, just click the{' '}
        <button class="mx-1 align-bottom">
          <Icon sprite="queue-top" class="mt-2 h-[24px] w-[24px] text-gray-400" />
        </button>{' '}
        button.
      </div>
    );

  const handleDrop = (
    draggedId: EpisodeCompoundId,
    targetId: EpisodeCompoundId,
    edge: Edge
  ) => {
    const currentIds = currentPlaylist.value.map(([id]) => ({ id }));
    const newIdsOrder = reorder(currentIds, draggedId, targetId, edge).map(
      ({ id }) => id
    );
    const newPlaylist = [...currentPlaylist.value];
    newPlaylist.sort((a, b) => newIdsOrder.indexOf(a[0]) - newIdsOrder.indexOf(b[0]));
    currentPlaylist.value = newPlaylist;
  };

  return (
    <div class="draggable-panel-ignore-pointerdown mx-auto flex max-w-md flex-col gap-3 pt-4">
      {currentPlaylist.value.map(([episodeId]) => {
        const episode = idToEpisode(episodeId);
        if (!episode) return <></>;
        return (
          <PlaylistEpisodeCard key={episode.id} episode={episode} onDrop={handleDrop} />
        );
      })}
    </div>
  );
}

function PlaylistEpisodeCard({
  episode,
  onDrop,
}: {
  episode: Episode;
  onDrop: (
    draggedId: EpisodeCompoundId,
    targetId: EpisodeCompoundId,
    edge: Edge
  ) => void;
}) {
  const { title } = episode;
  const podcastForThumbUrl = usePodcastsStore.getState().podcasts[episode.podcastId];
  const adsStatus = getAdsStatus(episode);

  const cardRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const { isDragging, closestEdge } = useDraggableHandle({
    cardRef,
    handleRef,
    id: episode.id,
    onDrop,
  });

  const onPlay = () => {
    // Pause current episode
    pause();
    // Play new episode instead
    const progress = useUserDataStore.getState().episodesProgress[episode.id];
    const [secondsPlayed = 0] = progress || [];
    const isPlayed =
      progress && progress.length ? checkIfPlayed(episode, progress) : false;
    const startingPosition = isPlayed ? 0 : secondsPlayed;
    loadNewEpisode(episode, startingPosition);
    play();
    trackPlayEpisodeInUpNextClick(episode);
    // Should automatically be dismissed from playlist...
  };

  return (
    <article ref={cardRef} class="relative flex gap-1 [&_*]:select-none">
      {/* drop indicators */}
      {closestEdge === 'top' && (
        <div class="absolute inset-x-0 -top-[2px] h-1 rounded bg-blue-500" />
      )}
      {closestEdge === 'bottom' && (
        <div class="absolute inset-x-0 -bottom-[2px] h-1 rounded bg-blue-500" />
      )}
      {/* Remove button */}
      <button
        class="m-auto mr-2 mt-2 shrink-0 rounded-full bg-gray-400 p-1"
        onClick={() => {
          currentPlaylist.value = currentPlaylist.value.filter(
            ([id]) => id !== episode.id
          );
        }}
        title="Remove from Up Next"
      >
        <Icon sprite="close" class="h-2.5 w-2.5 text-white" />
      </button>
      {/* Episode card */}
      <div
        class={classNames(
          'grow cursor-pointer',
          isDragging ? 'opacity-40' : 'opacity-100'
        )}
        tabIndex={0}
        onClick={onPlay}
        title="Click to play"
      >
        <img
          src={podcastForThumbUrl ? getThumbUrl(podcastForThumbUrl, 40) : ''}
          alt=""
          width="40"
          height="40"
          onClick={(e) => {
            // Go to podcast instead of episode when podcast thumbnail is clicked
            e.stopImmediatePropagation();
            e.preventDefault();
            route(`/podcast/${episode.podcastId}`);
          }}
          class={classNames(
            'float-left mb-0.5 mr-1.5 h-[34px] w-[34px] rounded-[4px] border border-[#e7e7e7]'
          )}
        />
        <h3 class={classNames('mb-0.5 text-pretty text-[14px] leading-[18px]')}>
          {title}
        </h3>
        <div
          class={classNames(
            'text-[13px] [@media(min-width:450px)]:text-[14px] [@media(min-width:500px)]:text-[14.5px]',
            'text-gray-800'
          )}
        >
          <time dateTime={episode.pubDate.toISOString()}>
            {relativeDate(episode.pubDate, 'short')}
          </time>
          <span>{adsStatus ? ` · ${adsStatus}` : ''}</span>
        </div>
      </div>
      {/* Drag handle */}
      <div class="flex max-w-12 shrink-0 items-center justify-center min-[460px]:grow">
        <div
          ref={handleRef}
          class={classNames(
            'w-full cursor-grab touch-none text-right [&_*]:pointer-events-none',
            'rounded p-2 text-gray-400 hover:text-gray-500 active:cursor-grabbing'
          )}
        >
          <Icon sprite="drag-up-down" class="inline-block h-4 w-[18px]" />
        </div>
      </div>
    </article>
  );
}
